import { useEffect } from 'react'
import { useForm } from 'react-hook-form'

import {
  MANAGEMENT_QUERY_KEY_SEARCH_UNIT_FIRMWARE,
  MANAGEMENT_QUERY_KEY_SEARCH_UNIT_FIRMWARES,
  useManagementCreateUnitFirmware,
  useManagementDeleteUnitFirmware,
  useManagementGetUnitFirmware,
  useManagementUpdateUnitFirmware
} from '@apps-common/apis/management/unit-firmware'
import { ButtonRoundedSm, ButtonRoundSm } from '@apps-common/components/elements/button'
import { CheckboxInputSM, InputSM } from '@apps-common/components/elements/form'
import { ConfirmModal, ConfirmModalAction, ModalButton } from '@apps-common/components/modals'
import {
  ModalBody,
  ModalCloseButton,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  ModalHeaderContainer
} from '@apps-common/components/modals/modal'
import { commonIcons } from '@apps-common/config/icons'
import { ModalDismiss } from '@apps-common/config/modal'
import { FORM_DEFAULT_REQUIRED } from '@apps-common/config/react-hook-form'
import { queryClient } from '@apps-common/lib'
import { IUnitFirmwareModel } from '@apps-common/types/management/unit-firmware'

export const UnitFirmwareModal = ({
  onDismiss,
  componentProps
}: {
  onDismiss: (data?: string | null | undefined | number, role?: string) => void
  componentProps: any
}) => {
  const {
    isLoading,
    isError,
    data: firmware
  } = useManagementGetUnitFirmware({
    firmwareId: componentProps?.unitFirmwareId || '',
    config: {
      enabled: !!componentProps?.unitFirmwareId
    }
  })

  const {
    reset,
    register,
    getValues,
    handleSubmit,
    formState: { errors }
  } = useForm<IUnitFirmwareModel>({})

  const onChanged = () => {
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_UNIT_FIRMWARE() })
    queryClient.invalidateQueries({ queryKey: MANAGEMENT_QUERY_KEY_SEARCH_UNIT_FIRMWARES() })
    onDismiss(null, ModalDismiss.Confirm)
  }

  const { isPending: createIsLoading, mutate: createFirmware } = useManagementCreateUnitFirmware(true, () => onChanged())
  const { isPending: updateIsLoading, mutate: updateFirmware } = useManagementUpdateUnitFirmware(true, () => onChanged())
  const { isPending: deleteIsLoading, mutate: deleteFirmware } = useManagementDeleteUnitFirmware(true, () => onChanged())

  useEffect(() => {
    if (firmware) {
      reset(firmware)
    }
  }, [firmware])

  const onSubmit = async (fwData: any) => {
    try {
      if (fwData.unitFirmwareId) {
        updateFirmware({ model: getValues() as any })
      } else {
        createFirmware({ model: getValues() as any })
      }
    } catch (e: any) {
      // ignore, let mutation handle error
    }
  }

  const loading = isLoading || isError
  const updating = createIsLoading || updateIsLoading || deleteIsLoading

  return (
    <ModalContainer innerClasses='!min-h-0 !h-auto'>
      <ModalHeaderContainer>
        <div className='flex items-center justify-between'>
          <ModalHeader subtitle={componentProps?.unitFirmwareId ? 'Updating' : 'Creating'} title='Firmware' subtitleClasses='capitalize' />
          <div className='flex items-center gap-[10px]'>
            {componentProps?.unitFirmwareId && (
              <ModalButton
                component={ConfirmModal}
                disabled={loading || updating}
                classes=''
                componentProps={{
                  text: 'Delete',
                  description: 'Confirm you would like to delete this firmware. This cannot be undone.',
                  action: ConfirmModalAction.Error
                }}
                confirmCallback={() => deleteFirmware({ firmwareId: componentProps?.unitFirmwareId })}
              >
                <ButtonRoundSm type='div' className='action-error'>
                  <commonIcons.delete />
                </ButtonRoundSm>
              </ModalButton>
            )}
            <ModalCloseButton closeModal={() => onDismiss(null, ModalDismiss.Cancel)} />
          </div>
        </div>
      </ModalHeaderContainer>
      <ModalBody classes='relative !pt-[110px] !pb-[80px]'>
        {!isLoading && isError && (
          <p className='mb-[10px] text-error text-sm'>Firmware version failed to load. Please close window and try again.</p>
        )}
        <form noValidate id='unitFirmwareModalForm' onSubmit={handleSubmit(onSubmit)}>
          <div>
            <InputSM
              type='text'
              placeholder='Version of firmware'
              label='Firmware version'
              id='name'
              showLabel
              loading={loading}
              disabled={updating}
              registration={register('unitFirmwareVersion', {
                required: FORM_DEFAULT_REQUIRED
              })}
              errors={errors.unitFirmwareVersion}
            />
            <CheckboxInputSM
              label='Is the default firmware version'
              id='defaultItem'
              loading={loading}
              disabled={updating}
              registration={register('defaultItem')}
              errors={errors.defaultItem}
            />
          </div>
        </form>
      </ModalBody>
      <ModalFooter>
        <div className='flex items-center justify-start gap-2'>
          <ButtonRoundedSm
            form='unitFirmwareModalForm'
            type='submit'
            loading={loading || updating}
            text={firmware ? 'Update' : 'Create'}
            className='action-success sm:grow-0'
          />
        </div>
      </ModalFooter>
    </ModalContainer>
  )
}
